/**
 * Language & Voice settings section.
 *
 * Provides:
 * - My Languages: toggle chips for preferred language selection
 * - Also Reply In: collapsible toggle chips for additional output languages
 * - Voice Gender: Male/Female toggle
 * - Voice Picker: per-language voice list with preview playback
 */

import { useState, useCallback, useRef } from 'react';
import { useSetting } from '../settings/useSetting';
import { STORAGE_KEYS } from '../utils/constants';
import { SUPPORTED_LANGUAGES, getVoicesForLanguage, PREVIEW_PHRASES } from '../voice/langDetect';
import type { LanguageInfo, VoiceOption } from '../voice/langDetect';
import { getEdgeTtsConfig } from '../voice/tts';
import { getApiAuthHeaders } from '../utils/auth';
import styles from './Settings.module.css';

// ── Constants ──

/** Top 8 most common languages shown by default */
const COMMON_LANGUAGE_CODES = ['en', 'da', 'de', 'fr', 'es', 'sv', 'nb', 'nl'];

// ── Helpers ──

function parseJsonArray(raw: string): string[] {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed as string[];
  } catch { /* ignore */ }
  return [];
}

function parseJsonRecord(raw: string): Record<string, string> {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, string>;
    }
  } catch { /* ignore */ }
  return {};
}

// ── Sub-components ──

function LanguageChips({
  languages: visibleLanguages,
  selected,
  onToggle,
  minSelected,
}: {
  languages: LanguageInfo[];
  selected: string[];
  onToggle: (code: string) => void;
  minSelected: number;
}) {
  return (
    <div className={styles.langGrid}>
      {visibleLanguages.map((lang) => {
        const isSelected = selected.includes(lang.code);
        const isDisabled = isSelected && selected.length <= minSelected;
        return (
          <button
            key={lang.code}
            type="button"
            className={`${styles.langChip} ${isSelected ? styles.langChipSelected : ''}`}
            onClick={() => {
              if (!isDisabled) onToggle(lang.code);
            }}
            style={isDisabled ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
          >
            <span className={styles.langChipFlag}>{lang.flag}</span>
            {lang.name}
          </button>
        );
      })}
    </div>
  );
}

function CollapsibleSection({
  title,
  children,
  defaultOpen = false,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div>
      <div
        className={styles.collapsibleHeader}
        onClick={() => setOpen(!open)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(!open);
          }
        }}
      >
        <span>{title}</span>
        <span className={`${styles.collapsibleArrow} ${open ? styles.collapsibleArrowOpen : ''}`}>
          ▶
        </span>
      </div>
      {open && children}
    </div>
  );
}

// ── Main Component ──

export function LanguageVoiceSettings() {
  const [preferredRaw, setPreferredRaw] = useSetting(STORAGE_KEYS.PREFERRED_LANGUAGES);
  const [alsoReplyRaw, setAlsoReplyRaw] = useSetting(STORAGE_KEYS.ALSO_REPLY_IN);
  const [voiceGender, setVoiceGender] = useSetting(STORAGE_KEYS.VOICE_GENDER);
  const [overridesRaw, setOverridesRaw] = useSetting(STORAGE_KEYS.VOICE_OVERRIDES);

  const preferred = parseJsonArray(preferredRaw);
  const effectivePreferred = preferred.length > 0 ? preferred : ['en'];
  const alsoReply = parseJsonArray(alsoReplyRaw);
  const overrides = parseJsonRecord(overridesRaw);

  const [showAllMyLangs, setShowAllMyLangs] = useState(false);
  const [showAllReplyLangs, setShowAllReplyLangs] = useState(false);

  // Voice preview state
  const [playingVoice, setPlayingVoice] = useState<string | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);

  // ── My Languages ──

  const togglePreferred = useCallback(
    (code: string) => {
      const current = effectivePreferred.slice();
      const idx = current.indexOf(code);
      if (idx >= 0) {
        if (current.length <= 1) return; // must keep at least 1
        current.splice(idx, 1);
      } else {
        current.push(code);
      }
      setPreferredRaw(JSON.stringify(current));
    },
    [effectivePreferred, setPreferredRaw],
  );

  // ── Also Reply In ──

  const toggleAlsoReply = useCallback(
    (code: string) => {
      const current = alsoReply.slice();
      const idx = current.indexOf(code);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(code);
      }
      setAlsoReplyRaw(JSON.stringify(current));
    },
    [alsoReply, setAlsoReplyRaw],
  );

  // ── Voice Gender ──

  const handleGenderChange = useCallback(
    (gender: 'female' | 'male') => {
      setVoiceGender(gender);
    },
    [setVoiceGender],
  );

  // ── Voice Overrides ──

  const handleVoiceOverride = useCallback(
    (langCode: string, voice: string) => {
      const current = { ...overrides };
      if (voice === '') {
        delete current[langCode];
      } else {
        current[langCode] = voice;
      }
      setOverridesRaw(JSON.stringify(current));
    },
    [overrides, setOverridesRaw],
  );

  // ── Voice Preview ──

  const stopPreview = useCallback(() => {
    try {
      sourceRef.current?.stop();
    } catch { /* ignore */ }
    sourceRef.current = null;
    setPlayingVoice(null);
  }, []);

  const playPreview = useCallback(
    async (voice: VoiceOption, langCode: string) => {
      // Stop any existing preview
      stopPreview();

      const phrase = (PREVIEW_PHRASES[langCode] ?? PREVIEW_PHRASES['en']!).replace(
        '{name}',
        voice.display,
      );

      setPlayingVoice(voice.name);

      try {
        const edgeConfig = getEdgeTtsConfig();
        const url = `${edgeConfig.backendUrl.replace(/\/+$/, '')}/api/tts`;

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            ...getApiAuthHeaders(STORAGE_KEYS.EDGE_TTS_AUTH_TOKEN),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ text: phrase, speed: 1.0, voice: voice.name }),
        });

        if (!response.ok) {
          console.warn('[voice-preview] TTS error:', response.status);
          setPlayingVoice(null);
          return;
        }

        const buffer = await response.arrayBuffer();

        if (!audioCtxRef.current) {
          audioCtxRef.current = new AudioContext();
        }
        const audioCtx = audioCtxRef.current;
        const audioBuffer = await audioCtx.decodeAudioData(buffer);
        const source = audioCtx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioCtx.destination);
        sourceRef.current = source;

        source.onended = () => {
          setPlayingVoice((current) => (current === voice.name ? null : current));
        };

        source.start();
      } catch (err) {
        console.warn('[voice-preview] Playback failed:', err);
        setPlayingVoice(null);
      }
    },
    [stopPreview],
  );

  // ── Visible languages ──

  const commonLanguages = SUPPORTED_LANGUAGES.filter((l) =>
    COMMON_LANGUAGE_CODES.includes(l.code),
  );
  const myLangsVisible = showAllMyLangs ? SUPPORTED_LANGUAGES : commonLanguages;

  // For "Also Reply In", filter out already-preferred languages
  const alsoReplyAllLanguages = SUPPORTED_LANGUAGES.filter(
    (l) => !effectivePreferred.includes(l.code),
  );
  const alsoReplyCommon = alsoReplyAllLanguages.filter((l) =>
    COMMON_LANGUAGE_CODES.includes(l.code),
  );
  const replyLangsVisible = showAllReplyLangs ? alsoReplyAllLanguages : alsoReplyCommon;

  // Active languages for voice picker
  const activeLangCodes = [...new Set([...effectivePreferred, ...alsoReply])];
  const activeLangs = activeLangCodes
    .map((code) => SUPPORTED_LANGUAGES.find((l) => l.code === code))
    .filter((l): l is LanguageInfo => l !== undefined);

  const effectiveGender: 'female' | 'male' = voiceGender === 'male' ? 'male' : 'female';

  return (
    <div>
      {/* My Languages */}
      <span className={styles.apiKeyHint} style={{ marginBottom: 6, display: 'block' }}>
        My Languages
      </span>
      <LanguageChips
        languages={myLangsVisible}
        selected={effectivePreferred}
        onToggle={togglePreferred}
        minSelected={1}
      />
      <button
        type="button"
        className={styles.showAllBtn}
        onClick={() => setShowAllMyLangs(!showAllMyLangs)}
      >
        {showAllMyLangs ? 'Show less' : `Show all (${SUPPORTED_LANGUAGES.length})`}
      </button>

      {/* Also Reply In */}
      <CollapsibleSection title="Also Reply In">
        <span className={styles.apiKeyHint} style={{ marginBottom: 6, display: 'block' }}>
          Additional languages the assistant can reply in
        </span>
        <LanguageChips
          languages={replyLangsVisible}
          selected={alsoReply}
          onToggle={toggleAlsoReply}
          minSelected={0}
        />
        {alsoReplyAllLanguages.length > alsoReplyCommon.length && (
          <button
            type="button"
            className={styles.showAllBtn}
            onClick={() => setShowAllReplyLangs(!showAllReplyLangs)}
          >
            {showAllReplyLangs ? 'Show less' : `Show all (${alsoReplyAllLanguages.length})`}
          </button>
        )}
      </CollapsibleSection>

      {/* Voice Gender */}
      <div style={{ marginTop: 12 }}>
        <span className={styles.apiKeyHint} style={{ marginBottom: 6, display: 'block' }}>
          Voice Gender
        </span>
        <div className={styles.engineSelector}>
          <button
            type="button"
            className={`${styles.engineOption} ${effectiveGender === 'female' ? styles.engineActive : ''}`}
            onClick={() => handleGenderChange('female')}
          >
            👩 Female
          </button>
          <button
            type="button"
            className={`${styles.engineOption} ${effectiveGender === 'male' ? styles.engineActive : ''}`}
            onClick={() => handleGenderChange('male')}
          >
            👨 Male
          </button>
        </div>
      </div>

      {/* Voice Picker */}
      {activeLangs.length > 0 && (
        <CollapsibleSection title="Voice Picker">
          {activeLangs.map((lang) => {
            const voices = getVoicesForLanguage(lang.code, effectiveGender);
            const currentOverride = overrides[lang.code] ?? '';
            const defaultVoice = effectiveGender === 'male' ? lang.maleVoice : lang.femaleVoice;
            const selectedVoice = currentOverride || defaultVoice;

            return (
              <div key={lang.code} style={{ marginBottom: 12 }}>
                <div className={styles.voiceRowLang} style={{ marginBottom: 6 }}>
                  <span>{lang.flag}</span>
                  <span>{lang.name}</span>
                </div>
                {voices.length <= 1 ? (
                  <div className={styles.voiceSingleLabel}>
                    {voices[0]?.display ?? defaultVoice}
                    <button
                      type="button"
                      className={`${styles.voicePreviewBtn} ${playingVoice === (voices[0]?.name ?? defaultVoice) ? styles.voicePreviewPlaying : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (voices[0]) {
                          if (playingVoice === voices[0].name) {
                            stopPreview();
                          } else {
                            playPreview(voices[0], lang.code);
                          }
                        }
                      }}
                      title="Preview voice"
                    >
                      🔊
                    </button>
                  </div>
                ) : (
                  voices.map((voice) => {
                    const isSelected = selectedVoice === voice.name;
                    return (
                      <div
                        key={voice.name}
                        className={`${styles.voiceItem} ${isSelected ? styles.voiceItemSelected : ''}`}
                        onClick={() => handleVoiceOverride(lang.code, voice.name)}
                      >
                        <span style={{ fontSize: 14 }}>{isSelected ? '●' : '○'}</span>
                        <span className={styles.voiceItemName}>{voice.display}</span>
                        {voice.locale && (
                          <span className={styles.voiceItemLocale}>{voice.locale}</span>
                        )}
                        <button
                          type="button"
                          className={`${styles.voicePreviewBtn} ${playingVoice === voice.name ? styles.voicePreviewPlaying : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (playingVoice === voice.name) {
                              stopPreview();
                            } else {
                              playPreview(voice, lang.code);
                            }
                          }}
                          title="Preview voice"
                        >
                          🔊
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            );
          })}
        </CollapsibleSection>
      )}
    </div>
  );
}
